import { Button, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import { ActivityFeedItem } from '../services/pusher.service';
import { TimestampDisplay } from './TimestampDisplay';
import { FeedEventTag } from './FeedEventTag';
import { getFeedItemDescription } from './feedItemDescription';
import './FeedTable.scss';

interface FeedTableProps {
  items: ActivityFeedItem[];
  /**
   * Omit the contact's name from every description. Set on a single contact's page,
   * where naming and linking the contact on every row is noise.
   */
  hideContact?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * Activity as a dense table - timestamp, type, description.
 *
 * The alternative to FeedList's cards, for surfaces reading a long run of events at once
 * rather than browsing the latest few. Both render the same sentence through
 * getFeedItemDescription, so the two views cannot describe an event differently.
 */
export function FeedTable({ items, hideContact, hasMore, isLoadingMore, onLoadMore }: FeedTableProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="feed-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell as="th">When</TableCell>
            <TableCell as="th">Type</TableCell>
            <TableCell as="th">Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} className="feed-table__row">
              <TableCell className="feed-table__when">
                <TimestampDisplay timestamp={item.deliveredAt} singleFormat />
              </TableCell>
              <TableCell className="feed-table__type">
                <FeedEventTag eventType={item.eventType} short />
              </TableCell>
              <TableCell className="feed-table__description">
                {getFeedItemDescription(item, { hideContact, hideEmoji: true, showScreenshot: true })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMore && onLoadMore && (
        <div className="feed-table__load-more">
          <Button size="small" className="silent" onClick={onLoadMore} isLoading={isLoadingMore}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
